import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Settings,
  Users,
  Cloud,
  Server,
  Search,
  RefreshCw,
  Check,
  AlertCircle,
  Star,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from './Dialog';
import { Button } from './Button';
import { Switch } from './Switch';
import { Badge } from './Badge';
import { Spinner } from './Spinner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import { useUpdateAiConfig } from '../helpers/useAiChat';
import { useSession, useUpdateSession } from '../helpers/useSessionsApi';
import { useAiModels } from '../helpers/useAiModels';
import { useUpdateUserAiConfig } from '../helpers/useUserSettings';
import styles from './AiConfigDialog.module.css';

// Schema for the form
const formSchema = z.object({
  aiEnabled: z.boolean(),
  allowParticipantPersonas: z.boolean(),
  aiProvider: z.string().optional(),
  aiModel: z.string().optional(),
  useLocalLlm: z.boolean().optional(),
});

type FormValues = {
  aiEnabled: boolean;
  allowParticipantPersonas: boolean;
  aiProvider?: string;
  aiModel?: string;
  useLocalLlm?: boolean;
};

interface AiConfigDialogProps {
  sessionId: string;
}

export const AiConfigDialog: React.FC<AiConfigDialogProps> = ({ sessionId }) => {
  const effectiveSessionId = sessionId && sessionId !== 'taskingbot-global' ? sessionId : undefined;
  const { data: sessionData } = useSession(effectiveSessionId);
  const { mutate: updateConfig, isPending: isUpdatingConfig } = useUpdateAiConfig();
  const { mutate: updateSession, isPending: isUpdatingSession } = useUpdateSession();
  const { mutate: updateUserAiConfig, isPending: isUpdatingUserConfig } = useUpdateUserAiConfig();
  const [open, setOpen] = React.useState(false);
  const { data: modelsData, isLoading: isLoadingModels, isRefetching, refetch } = useAiModels({
    enabled: open,
  });
  const [activeModelTab, setActiveModelTab] = useState<'cloud' | 'local'>('cloud');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const session = sessionData?.session;
  const isPending = isUpdatingConfig || isUpdatingSession;

  // Filter models based on search query
  const cloudModels = useMemo(() => {
    if (!modelsData?.cloudModels) return [];
    const safeQuery = typeof searchQuery === 'string' ? searchQuery.toLowerCase() : '';
    return modelsData.cloudModels.filter(
      (m) =>
        (typeof m.name === 'string' && m.name.toLowerCase().includes(safeQuery)) ||
        (typeof m.model === 'string' && m.model.toLowerCase().includes(safeQuery)),
    );
  }, [modelsData, searchQuery]);

  const localModels = useMemo(() => {
    if (!modelsData?.localModels) return [];
    const safeQuery = typeof searchQuery === 'string' ? searchQuery.toLowerCase() : '';
    return modelsData.localModels.filter(
      (m) =>
        (typeof m.name === 'string' && m.name.toLowerCase().includes(safeQuery)) ||
        (typeof m.model === 'string' && m.model.toLowerCase().includes(safeQuery)),
    );
  }, [modelsData, searchQuery]);

  const customModels = useMemo(() => {
    if (!modelsData?.customModels) return [];
    const safeQuery = typeof searchQuery === 'string' ? searchQuery.toLowerCase() : '';
    return modelsData.customModels.filter(
      (m) =>
        (typeof m.name === 'string' && m.name.toLowerCase().includes(safeQuery)) ||
        (typeof m.model === 'string' && m.model.toLowerCase().includes(safeQuery)),
    );
  }, [modelsData, searchQuery]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      aiEnabled: true,
      allowParticipantPersonas: false,
      aiProvider: 'ollama_cloud',
      aiModel: 'glm-5:cloud',
      useLocalLlm: false,
    },
  });

  // Update form when session data loads
  useEffect(() => {
    if (session) {
      const isLocal = session.aiProvider === 'ollama_local';
      form.reset({
        aiEnabled: session.aiEnabled ?? true,
        allowParticipantPersonas: session.allowParticipantPersonas ?? false,
        aiProvider: session.aiProvider ?? 'ollama_cloud',
        aiModel: session.aiModel ?? 'glm-5:cloud',
        useLocalLlm: isLocal,
      });
      setActiveModelTab(isLocal ? 'local' : 'cloud');
    }
  }, [session, form]);

  // Handle model selection
  const handleModelSelect = (provider: string, model: string, isLocal: boolean) => {
    form.setValue('aiProvider', provider);
    form.setValue('aiModel', model);
    form.setValue('useLocalLlm', isLocal);
  };

  // Refresh models when dialog opens
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  const onSubmit = (values: FormValues) => {
    if (!session || !effectiveSessionId) return;

    // Update AI config (aiEnabled, provider, model)
    updateConfig(
      {
        sessionId: effectiveSessionId,
        aiEnabled: values.aiEnabled,
        aiProvider: values.aiProvider ?? session.aiProvider ?? 'ollama_cloud',
        aiModel: values.aiModel ?? session.aiModel ?? 'glm-5:cloud',
        aiCustomEndpoint: session.aiCustomEndpoint ?? '',
      },
      {
        onSuccess: () => {
          // Update session settings (allowParticipantPersonas)
          updateSession(
            {
              id: effectiveSessionId,
              allowParticipantPersonas: values.allowParticipantPersonas,
            },
            {
              onSuccess: () => {
                // Show success message, then close after a brief delay
                setShowSuccess(true);
                setTimeout(() => {
                  setShowSuccess(false);
                  setOpen(false);
                }, 1200);
              },
            },
          );
        },
      },
    );
  };

  if (!effectiveSessionId) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon-sm" title="AI settings" aria-label="AI settings">
          <Settings size={14} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Session AI Settings</DialogTitle>
          <DialogDescription>
            Control the availability of AI features for this session.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.field}>
            <div className={styles.switchLabel}>
              <label htmlFor="aiEnabled" className={styles.label}>
                Enable AI Assistant
              </label>
              <span className={styles.description}>
                Allow participants to use AI features in this session.
              </span>
            </div>
            <Switch
              id="aiEnabled"
              checked={form.watch('aiEnabled')}
              onCheckedChange={(checked) => form.setValue('aiEnabled', checked)}
            />
          </div>

          <div className={styles.field}>
            <div className={styles.switchLabel}>
              <label htmlFor="allowParticipantPersonas" className={styles.label}>
                <Users size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Allow Participant Personas
              </label>
              <span className={styles.description}>
                When enabled, participants can use their own personal AI personas. When disabled,
                only session personas you enable will be applied.
              </span>
            </div>
            <Switch
              id="allowParticipantPersonas"
              checked={form.watch('allowParticipantPersonas')}
              onCheckedChange={(checked) => form.setValue('allowParticipantPersonas', checked)}
            />
          </div>

          {/* Model Selection Section */}
          <div className={styles.modelSection}>
            <div className={styles.modelHeader}>
              <div>
                <label className={styles.label}>AI Model</label>
                <span className={styles.description}>
                  Select which AI model to use for this session.
                </span>
              </div>
              <div className={styles.localLlmToggle}>
                <label htmlFor="localLlmEnabled" className={styles.smallLabel}>
                  <Server size={12} style={{ marginRight: 4 }} />
                  Local LLM
                </label>
                <Switch
                  id="localLlmEnabled"
                  checked={modelsData?.localEnabled ?? false}
                  disabled={isUpdatingUserConfig}
                  onCheckedChange={(checked) => {
                    // Update user AI settings to enable/disable local LLM
                    updateUserAiConfig(
                      {
                        localLlmEnabled: checked,
                      },
                      {
                        onSuccess: () => {
                          refetch();
                        },
                      },
                    );
                  }}
                />
              </div>
            </div>

            <Tabs
              value={activeModelTab}
              onValueChange={(v) => setActiveModelTab(v as 'cloud' | 'local')}
              className={styles.modelTabs}
            >
              <TabsList className={styles.modelTabsList}>
                <TabsTrigger value="cloud" className={styles.modelTabsTrigger}>
                  <Cloud size={14} />
                  Cloud
                </TabsTrigger>
                <TabsTrigger value="local" className={styles.modelTabsTrigger}>
                  <Server size={14} />
                  Local
                </TabsTrigger>
              </TabsList>

              {/* Search bar */}
              <div className={styles.searchWrapper}>
                <Search size={14} className={styles.searchIcon} />
                <input
                  className={styles.searchInput}
                  placeholder="Search models..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => refetch()}
                  title="Refresh models"
                  disabled={isRefetching || isLoadingModels}
                  className={isRefetching ? styles.spinning : ''}
                >
                  <RefreshCw size={14} />
                </Button>
              </div>

              <TabsContent value="cloud" className={styles.modelList}>
                {isLoadingModels ? (
                  <div className={styles.loadingModels}>
                    <Spinner size="sm" />
                    <span>Loading models...</span>
                  </div>
                ) : cloudModels.length > 0 ? (
                  cloudModels.map((m) => (
                    <button
                      key={`${m.provider}-${m.model}`}
                      type="button"
                      className={`${styles.modelItem} ${
                        form.watch('aiModel') === m.model && !form.watch('useLocalLlm')
                          ? styles.modelItemSelected
                          : ''
                      }`}
                      onClick={() => handleModelSelect(m.provider || 'openai', m.model, false)}
                    >
                      <div className={styles.modelInfo}>
                        <span className={styles.modelName}>{m.name}</span>
                        {m.provider && m.provider !== 'ollama_cloud' && (
                          <span className={styles.modelProvider}>{m.provider}</span>
                        )}
                      </div>
                      <div className={styles.modelTags}>
                        {m.tags?.map((t) => (
                          <Badge key={t} variant="secondary" className={styles.modelBadge}>
                            {t}
                          </Badge>
                        ))}
                        {form.watch('aiModel') === m.model && !form.watch('useLocalLlm') && (
                          <Check size={14} className={styles.checkIcon} />
                        )}
                      </div>
                    </button>
                  ))
                ) : customModels.length === 0 ? (
                  <div className={styles.emptyModels}>
                    No cloud models found{searchQuery && ` matching "${searchQuery}"`}
                  </div>
                ) : null}

                {/* Custom models section */}
                {customModels.length > 0 && (
                  <>
                    {cloudModels.length > 0 && (
                      <div className={styles.endpointInfo}>
                        <Star size={12} />
                        <span className={styles.endpointLabel}>Your Custom Models</span>
                      </div>
                    )}
                    {customModels.map((m) => (
                      <button
                        key={`custom-${m.provider}-${m.model}`}
                        type="button"
                        className={`${styles.modelItem} ${
                          form.watch('aiModel') === m.model && !form.watch('useLocalLlm')
                            ? styles.modelItemSelected
                            : ''
                        }`}
                        onClick={() => handleModelSelect(m.provider || 'openai', m.model, false)}
                      >
                        <div className={styles.modelInfo}>
                          <span className={styles.modelName}>{m.name}</span>
                          <span className={styles.modelProvider}>{m.provider}</span>
                        </div>
                        <div className={styles.modelTags}>
                          <Badge variant="outline" className={styles.modelBadge}>
                            Custom
                          </Badge>
                          {m.hasApiKey && (
                            <Badge variant="secondary" className={styles.modelBadge}>
                              Own Key
                            </Badge>
                          )}
                          {form.watch('aiModel') === m.model && !form.watch('useLocalLlm') && (
                            <Check size={14} className={styles.checkIcon} />
                          )}
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </TabsContent>

              <TabsContent value="local" className={styles.modelList}>
                {modelsData?.localEndpoint && (
                  <div className={styles.endpointInfo}>
                    <span className={styles.endpointLabel}>Connected to:</span>
                    <span className={styles.endpointUrl}>{modelsData.localEndpoint}</span>
                  </div>
                )}

                {modelsData?.error && (
                  <div className={styles.localError}>
                    <AlertCircle size={14} />
                    <span>{modelsData.error}</span>
                  </div>
                )}

                {isLoadingModels ? (
                  <div className={styles.loadingModels}>
                    <Spinner size="sm" />
                    <span>Loading models...</span>
                  </div>
                ) : localModels.length > 0 ? (
                  localModels.map((m) => (
                    <button
                      key={m.model}
                      type="button"
                      className={`${styles.modelItem} ${
                        form.watch('aiModel') === m.model && form.watch('useLocalLlm')
                          ? styles.modelItemSelected
                          : ''
                      }`}
                      onClick={() => handleModelSelect('ollama_local', m.model, true)}
                    >
                      <div className={styles.modelInfo}>
                        <span className={styles.modelName}>{m.name}</span>
                        {m.size && <span className={styles.modelProvider}>{m.size}</span>}
                      </div>
                      <div className={styles.modelTags}>
                        <Badge variant="outline" className={styles.modelBadge}>
                          Local
                        </Badge>
                        {form.watch('aiModel') === m.model && form.watch('useLocalLlm') && (
                          <Check size={14} className={styles.checkIcon} />
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className={styles.emptyModels}>
                    {modelsData?.localEnabled
                      ? isRefetching
                        ? 'Refreshing...'
                        : `No local models found${searchQuery ? ` matching "${searchQuery}"` : ''}`
                      : 'Local LLM is not enabled in settings'}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter>
            {showSuccess ? (
              <div className={styles.successMessage}>
                <Check size={16} />
                Applied successfully
              </div>
            ) : (
              <>
                <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
